/* ============================================================================
   TEMPERATURA DE SUPERFICIE — Open-Meteo.

   API pública y gratuita, sin clave. Se consulta la temperatura actual a 2 m
   sobre el terreno en las coordenadas del punto de despegue y del de
   aterrizaje.

     https://api.open-meteo.com/v1/forecast?latitude=..&longitude=..&current=temperature_2m

   Dos cosas que la app tiene que decir y no callar
   -----------------------------------------------

   1. Es un PRONÓSTICO de modelo numérico, no una observación. No es un METAR.
      Un METAR lo mide un instrumento en un aeródromo; esto es la salida de un
      modelo interpolada a un punto. Para performance sirve como estimación; no
      sirve como dato oficial.

   2. El modelo tiene su propio terreno, con su propia elevación, que puede
      diferir bastante de la del punto real —sobre todo en montaña, donde una
      celda de varios kilómetros promedia laderas y valles—. La respuesta trae
      esa elevación y acá se compara con la del punto: si difieren, se informa
      la diferencia y la corrección aproximada por gradiente térmico, para que
      el piloto decida. NO se corrige el número en silencio.
   ========================================================================== */

/* ═══════════════════════════════════════════════════════════════════════════
   LICENCIA DE OPEN-METEO — LO QUE HAY QUE SABER ANTES DE COBRAR

   Verificado en open-meteo.com/en/terms y /en/pricing:

     · La API gratuita es **solo para uso NO COMERCIAL**, con un tope de 10.000
       llamadas por día.
     · La API comercial corre en **otro servidor**: `customer-api.open-meteo.com`,
       con clave. No alcanza con pagar: hay que apuntar a otro endpoint.
     · Los datos son CC BY 4.0 y **la atribución es obligatoria en los dos
       casos**, gratuito y de pago. Sin ella el uso no está licenciado.

   El día que HeliCalc se cobre, seguir usando `api.open-meteo.com` es usar un
   servicio en violación de sus términos **para una función de seguridad**. No
   es un detalle administrativo.
   ══════════════════════════════════════════════════════════════════════════ */

/** La clave comercial del operador, si la tiene. */
const CLAVE = (import.meta.env?.VITE_OPENMETEO_KEY || "").trim();

/**
 * El endpoint correcto según haya clave o no.
 *
 * Con clave se usa el servidor de clientes: es el que trae la licencia
 * comercial. Apuntar al gratuito con una clave en el bolsillo sería pagar y
 * seguir en infracción.
 */
const BASE = CLAVE
  ? "https://customer-api.open-meteo.com/v1/forecast"
  : "https://api.open-meteo.com/v1/forecast";

/** ¿Está habilitado el uso comercial? */
export function usoComercialHabilitado() {
  return CLAVE !== "";
}

/**
 * La atribución obligatoria.
 *
 * Los datos son CC BY 4.0: hay que dar crédito con un enlace **al lado de
 * donde se muestran los datos**, no en una pantalla de créditos aparte. Es lo
 * que pide la licencia textualmente.
 */
export const ATRIBUCION_METEO = {
  texto: "Datos meteorológicos de Open-Meteo.com",
  url: "https://open-meteo.com/",
  licencia: "CC BY 4.0",
  licenciaUrl: "https://creativecommons.org/licenses/by/4.0/",
};

/** Gradiente térmico de la atmósfera estándar: 1,98 °C por cada 1000 ft. */
const GRADIENTE_C_POR_1000FT = 1.98;
const M_A_FT = 3.28084;

/**
 * Temperatura actual en un punto.
 *
 * @param {number} lat
 * @param {number} lon
 * @param {number|null} elevacionPuntoFt elevación real del punto, para comparar
 *        con la del modelo. Si no se pasa, no se calcula la diferencia.
 * @returns {Promise<object>} siempre resuelve; el error viaja en el objeto.
 */
export async function temperaturaEnSuperficie(lat, lon, elevacionPuntoFt = null) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { ok: false, error: "El punto no tiene coordenadas todavía." };
  }

  const url = `${BASE}?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}${CLAVE ? `&apikey=${CLAVE}` : ""}` +
    "&current=temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m" +
    "&wind_speed_unit=kn&timezone=UTC";

  try {
    // Límite de tiempo explícito: sin esto, una red que no responde ni falla
    // deja la promesa colgada para siempre y el botón girando.
    const control = new AbortController();
    const reloj = setTimeout(() => control.abort(), 8000);
    const respuesta = await fetch(url, { signal: control.signal });
    clearTimeout(reloj);

    if (!respuesta.ok) return { ok: false, error: `El servicio respondió ${respuesta.status}.` };

    const datos = await respuesta.json();
    const tempC = datos?.current?.temperature_2m;
    if (!Number.isFinite(tempC)) {
      return { ok: false, error: "La respuesta no trae temperatura." };
    }

    const elevModeloFt = Number.isFinite(datos.elevation) ? Math.round(datos.elevation * M_A_FT) : null;
    let diferenciaFt = null;
    let correccionC = null;
    if (Number.isFinite(elevacionPuntoFt) && elevModeloFt !== null) {
      diferenciaFt = elevacionPuntoFt - elevModeloFt;
      // Positivo = el punto está MÁS ALTO que el terreno del modelo, así que la
      // temperatura real sería MENOR. Se informa, no se aplica.
      correccionC = -(diferenciaFt / 1000) * GRADIENTE_C_POR_1000FT;
    }

    const vientoKt = datos?.current?.wind_speed_10m;
    const rafagaKt = datos?.current?.wind_gusts_10m;
    const direccion = datos?.current?.wind_direction_10m;

    return {
      ok: true,
      temperaturaC: Math.round(tempC * 10) / 10,
      vientoKt: Number.isFinite(vientoKt) ? Math.round(vientoKt) : null,
      rafagaKt: Number.isFinite(rafagaKt) ? Math.round(rafagaKt) : null,
      direccionGrados: Number.isFinite(direccion) ? Math.round(direccion) : null,
      /**
       * Lo que entra en el FRAT es el MAYOR entre viento sostenido y ráfaga.
       *
       * Un límite de 25 kt que se evalúa contra el viento medio deja pasar un
       * día de 20 kt con ráfagas de 35. La ráfaga es la que arranca el
       * helicóptero de la plataforma, así que manda ella.
       *
       * El viento es a 10 m sobre el terreno del modelo: no es el viento en la
       * plataforma ni a la altura del rotor.
       */
      vientoParaRiesgoKt: Math.max(
        Number.isFinite(vientoKt) ? vientoKt : 0,
        Number.isFinite(rafagaKt) ? rafagaKt : 0,
      ) || null,
      horaUtc: datos?.current?.time || null,
      elevacionModeloFt: elevModeloFt,
      diferenciaElevacionFt: diferenciaFt,
      correccionSugeridaC: correccionC === null ? null : Math.round(correccionC * 10) / 10,
      fuente: "Open-Meteo (pronóstico de modelo, no observación)",
    };
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, error: "El servicio de meteorología no respondió a tiempo." };
    return { ok: false, error: "Sin conexión con el servicio de meteorología." };
  }
}

/**
 * Pronóstico en ALTURA para el nivel de crucero.
 *
 * Pide temperatura, viento y ALTURA GEOPOTENCIAL en varios niveles de presión.
 * La altura geopotencial es la clave: la altitud de un nivel no es fija —el de
 * 500 hPa puede estar entre 5 400 y 5 800 m según el sistema de presión— así
 * que el nivel se elige por su altura real del día, no por la nominal.
 *
 * Devuelve los niveles crudos; la interpolación la hace el dominio, donde está
 * probada.
 */
export async function atmosferaEnAltura(lat, lon, nivelesHPa = [850, 700, 600, 500, 400]) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { ok: false, error: "El punto no tiene coordenadas todavía." };
  }

  const campos = nivelesHPa.flatMap((h) => [
    `temperature_${h}hPa`, `wind_speed_${h}hPa`, `wind_direction_${h}hPa`, `geopotential_height_${h}hPa`,
  ]);
  const url = `${BASE}?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}${CLAVE ? `&apikey=${CLAVE}` : ""}` +
    `&hourly=${campos.join(",")}&wind_speed_unit=kn&forecast_days=1&timezone=UTC`;

  try {
    const control = new AbortController();
    const reloj = setTimeout(() => control.abort(), 12000);
    const respuesta = await fetch(url, { signal: control.signal });
    clearTimeout(reloj);
    if (!respuesta.ok) return { ok: false, error: `El servicio respondió ${respuesta.status}.` };

    const datos = await respuesta.json();
    const horas = datos?.hourly?.time || [];
    if (horas.length === 0) return { ok: false, error: "La respuesta no trae pronóstico horario." };

    // Se toma la hora más cercana a AHORA, no la primera del día: a las 18:00
    // la primera fila es la de medianoche y no describe el vuelo de esta tarde.
    const ahora = Date.now();
    let iHora = 0;
    let mejor = Infinity;
    horas.forEach((t, i) => {
      const d = Math.abs(new Date(`${t}Z`).getTime() - ahora);
      if (d < mejor) { mejor = d; iHora = i; }
    });

    const M_A_FT = 3.28084;
    const niveles = nivelesHPa.map((h) => {
      const alturaM = datos.hourly?.[`geopotential_height_${h}hPa`]?.[iHora];
      return {
        hPa: h,
        alturaFt: Number.isFinite(alturaM) ? Math.round(alturaM * M_A_FT) : null,
        temperatura: datos.hourly?.[`temperature_${h}hPa`]?.[iHora] ?? null,
        velocidad: datos.hourly?.[`wind_speed_${h}hPa`]?.[iHora] ?? null,
        direccion: datos.hourly?.[`wind_direction_${h}hPa`]?.[iHora] ?? null,
      };
    }).filter((n) => Number.isFinite(n.alturaFt));

    if (niveles.length === 0) {
      return { ok: false, error: "El pronóstico no trae alturas geopotenciales: no se puede ubicar el nivel de crucero." };
    }

    return {
      ok: true,
      niveles,
      horaUtc: horas[iHora],
      fuente: "Open-Meteo, niveles de presión (pronóstico de modelo, no observación)",
    };
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, error: "El servicio de meteorología no respondió a tiempo." };
    return { ok: false, error: "Sin conexión con el servicio de meteorología." };
  }
}

/* ============================================================================
   VIENTO DE SUPERFICIE BAJO EL CURSOR

   El operador lo pidió junto a la elevación: mirar el viento de un punto sin
   tener que agregarlo a la ruta.

   ══════════════════════════════════════════════════════════════════════════
   SE CONSULTA POR CELDA, NO POR PUNTO

   Una consulta por píxel agotaría las 10.000 llamadas diarias en minutos —y
   con licencia comercial, agotaría el plan—.

   Redondeando a un cuarto de grado —unos 28 km— el viento de superficie no
   cambia de forma relevante para decidir si conviene mirar una zona. Mover el
   cursor dentro de la misma celda no pide nada.

   Y lo que se trae se guarda: volver sobre una celda ya consultada es gratis.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Tamaño de celda, en grados. */
const CELDA = 0.25;

/** Cuánto vale una consulta antes de repetirla. */
const VIGENCIA_MS = 30 * 60 * 1000;

const vientoPorCelda = new Map();

/** La celda de un punto. Es también la clave del caché. */
export function celdaDe(lat, lon) {
  /* ═══ `Number(null)` ES CERO — QUINTA VEZ EN ESTE PROYECTO ═══

     Con `Number(lat)` a secas, `celdaDe(null, 0)` devolvía la celda «0.00,0.00»
     —el golfo de Guinea— como si fuera una posición válida, y el viento de ahí
     se mostraría bajo el cursor.

     Se comprueba el valor CRUDO antes de convertir. */
  const crudo = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));

  const la = Math.round(crudo(lat) / CELDA) * CELDA;
  const lo = Math.round(crudo(lon) / CELDA) * CELDA;
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  return `${la.toFixed(2)},${lo.toFixed(2)}`;
}

/**
 * Viento de superficie en la celda de este punto.
 *
 * Devuelve `null` si no se pudo traer. **No se inventa un viento en calma**:
 * un cero ahí diría que no hay viento, y no saberlo es otra cosa.
 */
export async function vientoDeSuperficie(lat, lon, { ahora = Date.now() } = {}) {
  const clave = celdaDe(lat, lon);
  if (!clave) return null;

  const guardado = vientoPorCelda.get(clave);
  if (guardado && ahora - guardado.cuando < VIGENCIA_MS) return guardado.viento;

  const [la, lo] = clave.split(",").map(Number);
  const url = `${BASE}?latitude=${la}&longitude=${lo}`
    + `${CLAVE ? `&apikey=${CLAVE}` : ""}`
    + "&current=wind_speed_10m,wind_direction_10m&wind_speed_unit=kn";

  try {
    const r = await fetch(url);
    if (!r.ok) return null;

    const j = await r.json();
    const kt = Number(j?.current?.wind_speed_10m);
    const grados = Number(j?.current?.wind_direction_10m);

    if (!Number.isFinite(kt) || !Number.isFinite(grados)) return null;

    const viento = { kt, grados: Math.round(grados), celda: clave };
    vientoPorCelda.set(clave, { viento, cuando: ahora });

    /* El caché no crece sin techo: una sesión larga recorriendo el mapa
       juntaría miles de celdas. */
    if (vientoPorCelda.size > 300) {
      const primera = vientoPorCelda.keys().next().value;
      vientoPorCelda.delete(primera);
    }

    return viento;
  } catch {
    /* Sin red no hay viento, y eso NO es viento en calma. */
    return null;
  }
}
